import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { HeaderFilter } from "../../components/HeaderFilter";
import { useToast } from "../../components/Toast";
import { formatMoney } from "../../lib/money";
import { CatalogCreateDialog } from "./CatalogCreateDialog";
import { CatalogEditDialog } from "./CatalogEditDialog";
import type { CatalogHit, Category } from "../../api/types";

// 品項庫。這一頁讓人看得見庫裡有什麼、價格是多少,並且維護它 ——
// 業主原話:「品項庫 無法刪除/編輯 已加入的品項」。
//
// 真正的用法是在明細列的「摘要」欄打字帶入(規格 §3.1(3):輸入輔助,不是約束)。

const LIMIT = 50;

export function CatalogPage() {
  const { push } = useToast();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CatalogHit | null>(null);
  // 要刪哪一筆。**刪除一律先問過** —— 按錯一格就少一筆品項,而且沒有復原。
  const [deleting, setDeleting] = useState<CatalogHit | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // 勾選中的分類名稱。**多選**(業主:「我只要SHOW家具與設備」),
  // 一個都沒勾就是顯示全部。入口是分類欄表頭上的漏斗(HeaderFilter)。
  const [picked, setPicked] = useState<string[]>([]);

  const { data: categoryData } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get<Category[]>("/categories"),
  });
  const categories = Array.isArray(categoryData) ? categoryData : [];

  // 勾選的分類用逗號串成一個參數。空字串會被 buildQuery 略過,
  // 也就是「沒勾任何分類」時根本不帶這一欄 —— 帶一個空的上去,
  // 後端會去找一個名字是空字串的分類,畫面一筆都不剩。
  const categoriesParam = picked.join(",");

  const { data, isLoading, error } = useQuery({
    queryKey: ["catalog", keyword, categoriesParam],
    queryFn: () =>
      api.get<CatalogHit[]>("/catalog/search", {
        q: keyword,
        limit: LIMIT,
        categories: categoriesParam,
      }),
  });

  // 浮層裡的選項。順序跟著分類清單走,HeaderFilter 回傳的勾選也照這個順序排 ——
  // 同一組勾選每次都組出同一個字串,查詢快取才不會因為順序不同而各存一份。
  const categoryNames = categories.map((row) => row.name);

  const hits = Array.isArray(data) ? data : [];

  const remove = useMutation({
    mutationFn: (hit: CatalogHit) => api.del(`/catalog/items/${hit.variant_id}`),
    onSuccess: (_result, hit) => {
      push(`已刪除「${hit.item_name}」`, "success");
      queryClient.invalidateQueries({ queryKey: ["catalog"] });
      setDeleting(null);
    },
    // 失敗就把後端那句話留在確認視窗裡,不要默默關掉讓人以為刪掉了。
    onError: (err) =>
      setDeleteError(err instanceof ApiError ? err.message : "無法連線到伺服器,請稍後再試。"),
  });

  const askDelete = (hit: CatalogHit) => {
    setDeleteError(null);
    setDeleting(hit);
  };

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-xl font-semibold text-[var(--text)]">品項庫</h2>
        <Button
          type="button"
          variant="primary"
          onClick={() => setCreating(true)}
          icon={<Plus aria-hidden="true" size={16} />}
        >
          新增品項
        </Button>
      </div>
      <p className="m-0 text-sm text-[var(--text-2)]">
        價格掛在「規格」而不是品項:同一個品項底下可以有多種規格各自定價。
        在明細列的「摘要」欄打兩個字以上就會即時建議,帶入後每一格仍可自由改寫。
      </p>

      <div className="flex items-center gap-2 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5">
        <Search aria-hidden="true" size={16} className="shrink-0 text-[var(--text-3)]" />
        <input
          type="search"
          value={keyword}
          aria-label="搜尋品項"
          placeholder="搜尋品名或規格"
          onChange={(event) => setKeyword(event.target.value)}
          className="h-9 w-full min-w-0 border-0 bg-transparent text-sm text-[var(--text)] placeholder:text-[var(--text-3)]"
        />
      </div>

      {isLoading ? <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p> : null}
      {error ? (
        <p role="alert" className="m-0 text-sm text-[var(--danger)]">
          品項庫載入失敗,請稍後再試。
        </p>
      ) : null}

      {/* 勾了分類卻一筆都沒中的時候,表格**還是要在**:漏斗長在表頭上,
          整張表收掉就等於把唯一能取消篩選的入口一起收掉,使用者只剩重新整理
          這條路。所以只要還有勾選中的分類,就留著表頭。 */}
      {hits.length > 0 || picked.length > 0 ? (
        <div className="w-full overflow-x-auto rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)]">
          <table aria-label="品項庫" className="w-full min-w-[42rem] border-collapse text-left">
            <thead>
              <tr className="bg-[var(--surface-2)] text-xs font-medium text-[var(--text-2)]">
                {/* 分類欄的表頭帶一個漏斗(業主 2026-09-13:「應該是要直接在表頭
                    有一下漏斗(意思就是篩選的意思) 點要顯示的 這樣才對」)。
                    篩選的入口就在它作用的那一欄上面,而選項收在**自己會捲動、
                    有高度上限**的浮層裡 —— 分類可以無限新增(設計規格 §5.2),
                    再多也只是在浮層裡捲,不會像舊做法那樣把版面推爆。
                    條件送給後端篩,不是拿回來的前 50 筆在這裡篩:那樣勾了家具卻
                    一筆都沒有,只因為前 50 筆剛好全是別的分類。 */}
                <th scope="col" className="px-3 py-2">
                  <span className="inline-flex items-center gap-1">
                    分類
                    {categories.length > 0 ? (
                      <HeaderFilter
                        label="分類"
                        options={categoryNames}
                        selected={picked}
                        onChange={setPicked}
                      />
                    ) : null}
                  </span>
                </th>
                {["品名", "規格", "單位", "單價", "操作"].map((header) => (
                  <th key={header} scope="col" className="px-3 py-2">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hits.map((hit) => (
                <tr key={hit.variant_id} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2 text-sm text-[var(--text-2)]">{hit.category}</td>
                  <td className="px-3 py-2 text-sm text-[var(--text)]">{hit.item_name}</td>
                  <td className="px-3 py-2 text-sm text-[var(--text-2)]">{hit.spec}</td>
                  <td className="px-3 py-2 text-sm text-[var(--text-2)]">{hit.unit}</td>
                  <td className="tabular px-3 py-2 text-right text-sm text-[var(--text)]">
                    {formatMoney(hit.price)}
                  </td>
                  <td className="px-3 py-2">
                    {/* 只有圖示的按鈕一定要有 aria-label,而且要說得出是哪一筆 ——
                        一頁五十列都叫「編輯」的話,螢幕閱讀器使用者分不出自己按的是誰。 */}
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`編輯 ${hit.item_name} ${hit.spec}`}
                        onClick={() => setEditing(hit)}
                        icon={<Pencil aria-hidden="true" size={16} />}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`刪除 ${hit.item_name} ${hit.spec}`}
                        onClick={() => askDelete(hit)}
                        icon={<Trash2 aria-hidden="true" size={16} />}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* 「庫是空的」與「這組條件查不到」是兩件事。後者放兔子只會讓人以為
          品項被刪光了,所以只有沒下任何條件時才進 EmptyState。 */}
      {!isLoading && !error && hits.length === 0 ? (
        keyword.trim() === "" && picked.length === 0 ? (
          <EmptyState
            title="品項庫還是空的。"
            description="用右上角的「新增品項」建第一筆。價格掛在規格上,同一個品項可以有多種規格各自定價。"
          />
        ) : (
          <p className="m-0 text-sm text-[var(--text-3)]">沒有符合的品項。</p>
        )
      ) : null}

      <CatalogCreateDialog open={creating} onClose={() => setCreating(false)} />
      <CatalogEditDialog hit={editing} onClose={() => setEditing(null)} />

      {/* 刪除的確認。**兩種結果要先講明白**:沒被報價單用過的規格是真刪,
          用過的只會停用 —— quote_lines.variant_id 指著它,硬刪會讓歷史單據
          失去來源追溯(設計規格 §5.5)。只問一句「確定嗎」的話,使用者以為
          自己清乾淨了,實際上那一筆還在資料庫裡;或者以為只是隱藏,
          結果真的沒了。 */}
      <Dialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="刪除品項"
        maxWidth="max-w-md"
        closeOnBackdrop={false}
      >
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-[var(--text)]">
            確定要刪除「{deleting?.item_name} {deleting?.spec}」嗎?
          </p>
          <p className="m-0 text-sm text-[var(--text-2)]">
            沒有被報價單用過的品項會直接刪掉;已經被用過的改成停用 ——
            搜尋與清單不再出現,歷史單據仍然指得到它,金額也不會變。
          </p>
          {deleteError ? (
            <p
              role="alert"
              className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
            >
              {deleteError}
            </p>
          ) : null}
          <div className="mt-1 flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDeleting(null)}
              disabled={remove.isPending}
            >
              取消
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (deleting !== null) remove.mutate(deleting);
              }}
              icon={<Trash2 aria-hidden="true" size={16} />}
            >
              刪除品項
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
